import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import i18n from "../i18n";
import { useTheme } from "../providers/ThemeProvider";
import SidebarMenu from "./SidebarMenu";
import MachineInfo from "./MachineInfo";
import MachinePicture from "./MachinePicture";
import MachineFlowChain from "./MachineFlowChain";
import type { Machine } from "../services/BackendDataService";
import LineInfo from "./LineInfo";
import FockeLogoAnimated from "./FockeLogoAnimated";

import SearchField from "./SearchField";
import { useLiveLineStatus } from "../hooks/useLiveLineStatus";
import { headerStyles, cx } from "../styles";

// Language options with flag emojis and full names
const LANGUAGE_OPTIONS = [
  { code: "en", flag: "🇺🇸", name: "English", shortName: "EN" },
  { code: "de", flag: "🇩🇪", name: "Deutsch", shortName: "DE" },
  { code: "fr", flag: "🇫🇷", name: "Français", shortName: "FR" },
];

// Custom Language Selector Component
function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [, forceUpdate] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const currentLanguage =
    LANGUAGE_OPTIONS.find((lang) => lang.code === i18n.language) ||
    LANGUAGE_OPTIONS[0];

  // Compute dropdown position from button rect
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, []);

  // Recalc on open and on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside both button and portal dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode).then(() => {
      forceUpdate((prev) => prev + 1);
    });
    setIsOpen(false);
  };

  return (
    <>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        className={cx(
          "flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-all duration-200",
          "bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm",
          "border border-white/20 hover:border-white/30",
          "focus:outline-none focus:ring-2 focus:ring-white/30",
          "shadow-sm text-sm",
        )}
        aria-label="Select language"
      >
        <span className="font-medium text-xs">{currentLanguage.shortName}</span>
        <svg
          className={cx(
            "w-3 h-3 transition-transform duration-200",
            isOpen ? "rotate-180" : "rotate-0",
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown rendered via portal — escapes overflow-hidden header */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={cx(
              "fixed py-1 w-40",
              currentTheme.isDark
                ? "bg-gray-800/95 backdrop-blur-md border-gray-600/30"
                : "bg-white/95 backdrop-blur-md border-white/30",
              "rounded-md shadow-xl border text-sm",
              "z-[9999]",
            )}
            style={{ top: pos.top, right: pos.right }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.code}
                onClick={() => handleLanguageChange(option.code)}
                className={cx(
                  "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors",
                  currentTheme.isDark
                    ? cx(
                        "hover:bg-gray-700/50 text-gray-100",
                        option.code === i18n.language
                          ? "bg-blue-800/50 text-blue-200 font-medium"
                          : "text-gray-200",
                      )
                    : cx(
                        "hover:bg-blue-50 text-gray-800",
                        option.code === i18n.language
                          ? "bg-blue-100 text-blue-900 font-medium"
                          : "text-gray-700",
                      ),
                )}
              >
                <span className="text-sm">{option.flag}</span>
                <div className="flex-1">
                  <div className="font-medium text-xs">{option.name}</div>
                </div>
                {option.code === i18n.language && (
                  <svg
                    className={cx(
                      "w-3 h-3",
                      currentTheme.isDark ? "text-blue-400" : "text-blue-600",
                    )}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// Top bar: menu, company, icons
function HeaderTopBar({
  currentView: _currentView,
  menuOpen,
  onMenuToggle,
}: {
  currentView: string;
  menuOpen: boolean;
  onMenuToggle: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [searchExpanded, setSearchExpanded] = useState(false);

  const handleMenuToggle = () => {
    const newMenuOpen = !menuOpen;
    onMenuToggle(newMenuOpen);
  };

  const handleTitleClick = () => {
    navigate("/");
  };

  // Animated Hamburger Icon component
  function HamburgerIcon({ open }: { open: boolean }) {
    return (
      <span className={headerStyles.hamburger.icon}>
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.top
              : headerStyles.hamburger.states.closed.top,
          )}
        />
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.middle
              : headerStyles.hamburger.states.closed.middle,
          )}
        />
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.bottom
              : headerStyles.hamburger.states.closed.bottom,
          )}
        />
      </span>
    );
  }

  // Removed RefreshButton component - was for testing purposes

  return (
    <div
      className={cx(
        headerStyles.topBar.container,
        "px-1 sm:px-4 md:px-8 lg:px-10",
      )}
      style={{ zIndex: 100 }}
    >
      {/* Mobile: Compact Layout */}
      <div className="flex flex-col gap-2 md:hidden w-full py-1">
        {/* Top row: Hamburger + Title + Language */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className={headerStyles.hamburger.button}
              onClick={handleMenuToggle}
              aria-label="Open menu"
            >
              <HamburgerIcon open={menuOpen} />
            </button>
            <h1
              className={cx(
                headerStyles.topBar.title,
                "cursor-pointer hover:opacity-80 transition-opacity duration-200",
                "text-sm truncate",
              )}
              onClick={handleTitleClick}
            >
              Cockpit
            </h1>
          </div>
          <LanguageSelector />
        </div>

        {/* Bottom row: Search */}
        <div className="flex items-center gap-2 w-full">
          <div className={searchExpanded ? "flex-1" : "flex-shrink-0"}>
            <SearchField onExpandedChange={setSearchExpanded} />
          </div>
        </div>
      </div>

      {/* Desktop: Horizontal Layout */}
      <div className="hidden md:contents">
        {/* Left: Hamburger Menu and Performance Cockpit Title */}
        <div className={cx(headerStyles.topBar.left, "flex-shrink-0")}>
          <button
            className={headerStyles.hamburger.button}
            onClick={handleMenuToggle}
            aria-label="Open menu"
          >
            <HamburgerIcon open={menuOpen} />
          </button>
          <h1
            className={cx(
              headerStyles.topBar.title,
              "cursor-pointer hover:opacity-80 transition-opacity duration-200",
              "text-sm sm:text-base md:text-lg lg:text-xl truncate max-w-[120px] sm:max-w-none",
            )}
            onClick={handleTitleClick}
          >
            <span className="hidden sm:inline">Performance Cockpit</span>
            <span className="sm:hidden">Cockpit</span>
          </h1>
        </div>

        {/* Center: Date Range Picker Button and Search Link */}
        <div
          className={cx(
            headerStyles.topBar.center,
            "flex",
            "top-3 sm:top-4 md:top-3 lg:top-3",
          )}
          style={{ zIndex: 100 }}
        >
          <SearchField />
        </div>

        {/* Right: Language selector and Animated Focke Logo */}
        <div className={cx(headerStyles.topBar.right, "flex-shrink-0")}>
          <LanguageSelector />
          <div className="hidden md:block">
            <FockeLogoAnimated />
          </div>
        </div>
      </div>
    </div>
  );
}

function MachineCard({
  scrolled,
  machineData,
}: {
  scrolled: boolean;
  machineData?: {
    machineId?: string;
    machineName?: string;
    lineId?: string;
    lineName?: string;
    lineNumber?: string;
    machineType?: string;
    factory?: string;
  };
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Format line display: show lineNumber with lineName if both available
  const lineDisplay =
    machineData?.lineNumber && machineData?.lineName
      ? `${machineData.lineNumber} - ${machineData.lineName}`
      : machineData?.lineNumber ||
        machineData?.lineName ||
        machineData?.lineId ||
        "TP01";

  // Strip the redundant machine number from the pre-formatted name
  // ("20055 - Modell 350 (20055)" → "Modell 350") for the compact subtitle.
  const escId = String(machineData?.machineId ?? "").replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );
  const compactModel = escId
    ? (machineData?.machineName || "")
        .replace(new RegExp(`^\\s*${escId}\\s*-\\s*`), "")
        .replace(new RegExp(`\\s*\\(${escId}\\)\\s*$`), "")
        .trim()
    : machineData?.machineName || "";

  const handleLineClick = () => {
    if (machineData?.lineId) {
      navigate(`/line/${machineData.lineId}`, {
        state: {
          lineName: machineData.lineName,
          lineNumber: machineData.lineNumber,
          factory: machineData.factory,
        },
      });
    }
  };

  return (
    <div
      className={cx(
        headerStyles.machineCard.base,
        scrolled
          ? headerStyles.machineCard.scrolled
          : headerStyles.machineCard.normal,
      )}
      style={{ willChange: "transform, opacity, min-height" }}
    >
      {/* Info left */}
      <div className={headerStyles.machineCard.info.container}>
        {scrolled ? (
          // Compact horizontal layout when scrolled
          <div className={headerStyles.machineCard.info.compact}>
            <div className={headerStyles.machineCard.info.title}>
              {machineData?.machineId || "56881"}
            </div>
            <div className={headerStyles.machineCard.info.subtitle}>
              {compactModel && <span>{compactModel}</span>}
              <span
                onClick={handleLineClick}
                className="cursor-pointer hover:text-white hover:underline transition-colors"
              >
                Line: {lineDisplay}
              </span>
            </div>
          </div>
        ) : (
          // Full layout when not scrolled
          <MachineInfo {...machineData} />
        )}
      </div>
      {/* Picture positioned slightly below center - only visible when not scrolled and on larger screens */}
      {!scrolled && (
        <div
          className={cx(
            headerStyles.machineCard.picture.container,
            "-translate-x-1/2 -translate-y-1/2 transition-all duration-300 ease-out",
            "hidden md:block",
          )}
        >
          <MachinePicture
            key={`${machineData?.machineId}-${machineData?.machineType}`}
            machineId={machineData?.machineId}
            machineType={machineData?.machineType}
          />
        </div>
      )}
      {/* Spacer for layout, button is now absolutely positioned */}
      <div className="hidden md:block" />
      {/* Service Portal button - fixed to viewport bottom, hidden on mobile */}
      <a
        href="https://serviceportal.focke.com/de/login"
        target="_blank"
        rel="noopener noreferrer"
        className={cx(
          headerStyles.servicePortal.style,
          headerStyles.servicePortal.hover,
          "fixed right-8 bottom-1 z-50 hidden md:block", // Always fixed to viewport bottom, hidden on mobile
        )}
        style={{ pointerEvents: "auto" }}
      >
        {t("servicePortal")}
      </a>
    </div>
  );
}

function LineCard({
  scrolled,
  lineData,
}: {
  scrolled: boolean;
  lineData?: {
    lineId?: string;
    lineNumber?: string;
    lineName?: string;
    factory?: string;
    machineCount?: number;
    lineType?: string;
    supervisor?: string;
    machines?: Machine[];
    oemNumbers?: string[];
  };
}) {
  const { t } = useTranslation();

  // Get live NATS status for all machines in the line
  const machineIds = React.useMemo(
    () =>
      (lineData?.machines || [])
        .map((m) => m.maschinennummer || m.maschineBK || "")
        .filter(Boolean),
    [lineData?.machines],
  );
  const liveStatuses = useLiveLineStatus(machineIds);
  return (
    <div
      className={cx(
        headerStyles.lineCard.base,
        scrolled
          ? headerStyles.lineCard.scrolled
          : headerStyles.lineCard.normal,
      )}
      style={{ willChange: "transform, opacity, min-height" }}
    >
      {/* Info left */}
      <div className={headerStyles.lineCard.info.container}>
        {scrolled ? (
          <div className={headerStyles.lineCard.info.compact}>
            <div className={headerStyles.lineCard.info.title}>
              {lineData?.lineNumber || lineData?.lineId || "N/A"}
            </div>
            <div className={headerStyles.lineCard.info.subtitle}>
              {lineData?.lineName && <span>{lineData.lineName}</span>}
              <span>Machines: {lineData?.machineCount || 4}</span>
            </div>
          </div>
        ) : (
          <LineInfo {...lineData} />
        )}
      </div>
      {/* Machine flow chain - centered on desktop (absolute), below info on mobile (flow) */}
      {!scrolled && lineData?.machines && lineData.machines.length > 0 && (
        <>
          {/* Mobile: below line info, scrollable */}
          <div className="w-full flex justify-center mt-2 md:hidden">
            <MachineFlowChain
              machines={lineData.machines}
              lineId={lineData.lineId}
              lineName={lineData.lineName}
              factory={lineData.factory}
              liveStatuses={liveStatuses}
            />
          </div>
          {/* Desktop: absolutely centered */}
          <div className="absolute inset-0 hidden md:flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <MachineFlowChain
                machines={lineData.machines}
                lineId={lineData.lineId}
                lineName={lineData.lineName}
                factory={lineData.factory}
                liveStatuses={liveStatuses}
              />
            </div>
          </div>
        </>
      )}
      {/* Spacer for layout, button is now absolutely positioned */}
      <div className="hidden md:block" />
      {/* Service Portal button - fixed to viewport bottom, hidden on mobile */}
      <a
        href="https://serviceportal.focke.com/de/login"
        target="_blank"
        rel="noopener noreferrer"
        className={cx(
          headerStyles.servicePortal.style,
          headerStyles.servicePortal.hover,
          "fixed right-8 bottom-1 z-50 hidden md:block",
        )}
        style={{ pointerEvents: "auto" }}
      >
        {t("servicePortal")}
      </a>
    </div>
  );
}

const Header: React.FC<{
  currentView: string;
  machineData?: {
    machineId?: string;
    machineName?: string;
    lineId?: string;
    lineName?: string;
    lineNumber?: string;
    machineType?: string;
    factory?: string;
  };
  lineData?: {
    lineId?: string;
    lineNumber?: string;
    lineName?: string;
    factory?: string;
    machineCount?: number;
    lineType?: string;
    supervisor?: string;
    machines?: Machine[];
  };
}> = ({ currentView, machineData, lineData }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { currentTheme } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show machine info only on machine view
  const showMachineCard = currentView === "machine";
  // Show line info only on line view
  const showLineCard = currentView === "line";
  const showAnyCard = showMachineCard || showLineCard;

  // Track previous view to detect changes
  const [prevView, setPrevView] = useState(currentView);

  // Track when cards should be visible with animation
  const [cardsVisible, setCardsVisible] = useState(showAnyCard);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setScrolled(scrollY > 30);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle initial mount - if we start with cards, show them immediately
  useEffect(() => {
    if (prevView === currentView && showAnyCard) {
      setCardsVisible(true);
    }
  }, []);

  // Sync cardsVisible with showAnyCard for initial render
  useEffect(() => {
    // If cards should be visible and previous view equals current view (no transition), show them
    if (showAnyCard && prevView === currentView && !cardsVisible) {
      setCardsVisible(true);
    }
  }, [showAnyCard, cardsVisible, prevView, currentView]);

  // Animate header height when view changes
  useEffect(() => {
    // Only animate if view actually changed
    if (currentView !== prevView) {
      // Update previous view
      setPrevView(currentView);

      // If contracting (hiding cards), hide cards immediately
      if (!showAnyCard) {
        setCardsVisible(false);
      }
      // If expanding (showing cards), show cards after a brief delay
      else {
        // Start with cards hidden during expansion
        setCardsVisible(false);
        // Show cards after a brief delay
        const timer = setTimeout(() => {
          setCardsVisible(true);
        }, 200);

        return () => clearTimeout(timer);
      }
    }
  }, [currentView, showAnyCard, prevView]);

  return (
    <>
      <div
        ref={headerRef}
        className={headerStyles.container}
        style={{
          background: currentTheme.isDark
            ? headerStyles.backgroundGradientDark
            : headerStyles.backgroundGradient,
          height: showAnyCard
            ? isMobile
              ? "200px"
              : "280px"
            : isMobile
              ? "110px"
              : "80px",
          clipPath:
            scrolled && showAnyCard
              ? isMobile
                ? "polygon(0 0, 100% 0, 100% 120px, 0 120px)"
                : "polygon(0 0, 100% 0, 100% 120px, 0 120px)"
              : "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
          transition:
            "clip-path 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), height 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)",
        }}
      >
        <HeaderTopBar
          currentView={currentView}
          menuOpen={menuOpen}
          onMenuToggle={setMenuOpen}
        />
        {showMachineCard && (
          <div
            className={cx(
              "transition-all duration-500 ease-out flex-1 flex flex-col min-h-0",
              scrolled ? "justify-start" : "justify-center",
            )}
            style={{
              transform: cardsVisible ? "translateY(0)" : "translateY(-20px)",
              opacity: cardsVisible ? 1 : 0,
            }}
          >
            <MachineCard scrolled={scrolled} machineData={machineData} />
          </div>
        )}
        {showLineCard && (
          <div
            className={cx(
              "transition-all duration-500 ease-out flex-1 flex flex-col min-h-0",
              scrolled ? "justify-start" : "justify-center",
            )}
            style={{
              transform: cardsVisible ? "translateY(0)" : "translateY(-20px)",
              opacity: cardsVisible ? 1 : 0,
            }}
          >
            <LineCard scrolled={scrolled} lineData={lineData} />
          </div>
        )}
      </div>
      <SidebarMenu
        currentView={currentView}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      />
    </>
  );
};

export default Header;
